/**
 * Вкладка «Сопоставление» каталога программ обучения (training-catalog-v2; ответ заказчицы
 * 27.10, В7 — «сроки обучения сотрудников перейдут на новые программы, сданные заказы останутся
 * без изменений»). Список архивных программ без замены, на которые ещё ссылаются записи обучения
 * или незавершённые заказы; для каждой администратор выбирает новую программу каталога. После
 * подтверждения записи обучения и черновики переходят на новую программу (сроки пересчитываются
 * по её периодичности), а заказ, поданный по прежней программе, при завершении даёт запись сразу
 * на новой. Правит ТОЛЬКО админ.
 *
 * Прежнюю программу-пакет («пакет из 3-х программ: "А", "Б" и первая помощь») одной новой не
 * заменить — её раскладывают на программы каталога (training-program-split; ответ заказчицы 29.10):
 * записи обучения становятся записями программ состава с той же датой обучения, заказ по ней при
 * завершении даёт записи по ним. Черновики с прежней программой не меняются.
 */
import { useEffect, useMemo, useState } from 'react'
import { ArrowRightLeft, CheckCircle2, Plus, Split, X } from 'lucide-react'
import {
  adminApi,
  trainingApi,
  type Program,
  type ProgramReplacementCandidate,
  type TrainingDirectionItem,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { directionPathLabel, flattenDirections, programLine } from '@/lib/training-catalog'
import { Panel } from '@/design-system/components/layout/panel'
import { Button } from '@/design-system/components/primitives/button'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { ProgramPickerDialog } from './catalog-shared'

export function ReplacementsTab() {
  const [candidates, setCandidates] = useState<ProgramReplacementCandidate[]>([])
  const [programs, setPrograms] = useState<Program[]>([])
  const [directions, setDirections] = useState<TrainingDirectionItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const [picking, setPicking] = useState<ProgramReplacementCandidate | null>(null)
  const [pending, setPending] = useState<{ from: ProgramReplacementCandidate; to: Program } | null>(null)
  // Разложение: выбор программ состава, затем подтверждение.
  // Выбранные программы сохраняются, если на подтверждении нажать «Отмена» — выбор открывается снова.
  const [splitting, setSplitting] = useState<{ from: ProgramReplacementCandidate; chosen: Program[] } | null>(null)
  const [pendingSplit, setPendingSplit] = useState<{ from: ProgramReplacementCandidate; to: Program[] } | null>(null)
  // Выбор, на котором разложение не удалось: повторное «Разложить на программы» начинается с него.
  const [failedSplit, setFailedSplit] = useState<{ fromId: string; chosen: Program[] } | null>(null)
  const [busy, setBusy] = useState(false)

  const nodes = useMemo(() => flattenDirections(directions), [directions])
  // Замена — только действующая программа каталога (не архивная и не на подтверждении).
  const active = useMemo(() => programs.filter((p) => !p.archived && p.status === 'active'), [programs])

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([
      adminApi.training.listProgramReplacements(controller.signal),
      trainingApi.listPrograms(controller.signal),
      trainingApi.listDirections(controller.signal),
    ])
      .then(([c, p, d]) => {
        if (controller.signal.aborted) return
        setCandidates(c)
        setPrograms(p)
        setDirections(d)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить список'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  async function apply() {
    if (!pending) return
    setActionError(null)
    setBusy(true)
    try {
      const res = await adminApi.training.replaceProgram(pending.from.id, pending.to.id)
      setCandidates((prev) => prev.filter((c) => c.id !== pending.from.id))
      setDone(
        `«${pending.from.name}» → «${programLine(pending.to)}»: перенесено записей обучения — ${res.recordsMoved}, строк черновиков — ${res.linesMoved + res.linesMerged}${res.packagesMoved ? `, пакетов — ${res.packagesMoved}` : ''}.`,
      )
      setPending(null)
    } catch (err) {
      // Окно подтверждения закрывается — сообщение видно над списком.
      setPending(null)
      setActionError(getErrorMessage(err, 'Не удалось сопоставить программу'))
    } finally {
      setBusy(false)
    }
  }

  /** С подтверждения разложения — обратно к выбору программ, выбранное сохраняется. */
  function backToSplitChoice() {
    if (pendingSplit) setSplitting({ from: pendingSplit.from, chosen: pendingSplit.to })
    setPendingSplit(null)
  }

  async function applySplit() {
    if (!pendingSplit) return
    setActionError(null)
    setBusy(true)
    try {
      const res = await adminApi.training.splitProgram(
        pendingSplit.from.id,
        pendingSplit.to.map((p) => p.id),
      )
      setCandidates((prev) => prev.filter((c) => c.id !== pendingSplit.from.id))
      setDone(
        `«${pendingSplit.from.name}» разложена на: ${res.components.map((p) => `«${programLine(p)}»`).join(', ')}. Записей обучения разложено — ${res.recordsSplit}, создано — ${res.recordsCreated}.`,
      )
      setPendingSplit(null)
    } catch (err) {
      setFailedSplit({ fromId: pendingSplit.from.id, chosen: pendingSplit.to })
      setPendingSplit(null)
      setActionError(getErrorMessage(err, 'Не удалось разложить программу'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <p className="mb-4 text-14 text-txt-secondary">
        Программы, которые ушли в архив, но по ним есть записи обучения, незавершённые заказы или пакеты. Выберите для
        каждой новую программу каталога — сроки обучения сотрудников и состав пакетов перейдут на неё. Прежнюю
        программу-пакет разложите на программы каталога — её записи обучения станут записями этих программ. Поданные
        заказы не меняются.
      </p>
      {done ? (
        <Alert variant="info" className="mb-4">
          {done}
        </Alert>
      ) : null}
      {actionError ? (
        <Alert variant="danger" className="mb-4">
          {actionError}
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading ? (
        <Panel>
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        </Panel>
      ) : candidates.length === 0 ? (
        <Panel>
          <EmptyState
            icon={CheckCircle2}
            title="Сопоставлять нечего"
            description="Все программы со ссылками на них действуют или уже сопоставлены с новыми"
          />
        </Panel>
      ) : (
        <Panel>
          <ul>
            {candidates.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3 last:border-b-0">
                <div className="min-w-0 flex-1">
                  <div className="break-words font-medium text-txt">
                    {c.code ? <span className="mr-2 font-normal text-txt-secondary">{c.code}</span> : null}
                    {c.name}
                  </div>
                  <div className="text-12 text-txt-tertiary">
                    {c.directionName} · записей обучения: {c.recordsCount} · в незавершённых заказах: {c.openLinesCount}
                    {c.packagesCount ? ` · в пакетах: ${c.packagesCount}` : ''}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setPicking(c)}
                    leftIcon={<ArrowRightLeft className="size-4 stroke-current" aria-hidden />}
                  >
                    Выбрать новую программу
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      setSplitting({ from: c, chosen: failedSplit?.fromId === c.id ? failedSplit.chosen : [] })
                    }
                    leftIcon={<Split className="size-4 stroke-current" aria-hidden />}
                  >
                    Разложить на программы
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {picking ? (
        <ProgramPickerDialog
          title="Новая программа вместо прежней"
          subtitle={`Прежняя: «${picking.name}»`}
          programs={active}
          sectionOf={(p) => directionPathLabel(nodes, p.directionId) || p.directionName}
          onClose={() => setPicking(null)}
          onPick={(p) => {
            setPending({ from: picking, to: p })
            setPicking(null)
          }}
        />
      ) : null}
      {splitting ? (
        <SplitDialog
          from={splitting.from}
          initial={splitting.chosen}
          programs={active}
          sectionOf={(p) => directionPathLabel(nodes, p.directionId) || p.directionName}
          onClose={() => setSplitting(null)}
          onNext={(to) => {
            setPendingSplit({ from: splitting.from, to })
            setSplitting(null)
          }}
        />
      ) : null}
      <ConfirmDialog
        open={pendingSplit !== null}
        title="Разложить на программы?"
        message={
          pendingSplit
            ? `Записи обучения с «${pendingSplit.from.name}» станут записями программ ${pendingSplit.to.map((p) => `«${programLine(p)}»`).join(', ')} с той же датой обучения, сроки — по периодичности каждой. Заказы, поданные по прежней программе, при завершении дадут записи по этим программам. Черновики с прежней программой не меняются. Отменить разложение нельзя.`
            : undefined
        }
        buttons={[
          { label: busy ? 'Разложение…' : 'Разложить', variant: 'primary', disabled: busy, onClick: () => void applySplit() },
          { label: 'Назад', variant: 'secondary', disabled: busy, onClick: backToSplitChoice },
        ]}
        onClose={backToSplitChoice}
        closeDisabled={busy}
      />
      <ConfirmDialog
        open={pending !== null}
        title="Перенести на новую программу?"
        message={
          pending
            ? `Записи обучения, черновики заказов и пакеты с «${pending.from.name}» перейдут на «${programLine(pending.to)}», сроки пересчитаются по её периодичности. Отменить перенос нельзя.`
            : undefined
        }
        buttons={[
          { label: busy ? 'Перенос…' : 'Перенести', variant: 'primary', disabled: busy, onClick: () => void apply() },
          { label: 'Отмена', variant: 'secondary', disabled: busy, onClick: () => setPending(null) },
        ]}
        onClose={() => setPending(null)}
        closeDisabled={busy}
      />
    </div>
  )
}

/**
 * Выбор программ, на которые раскладывается прежняя программа-пакет: не меньше двух, в порядке
 * выбора; программа добавляется поиском по каталогу (только действующие).
 */
function SplitDialog({
  from,
  initial,
  programs,
  sectionOf,
  onNext,
  onClose,
}: {
  from: ProgramReplacementCandidate
  /** Выбранные ранее (возврат с подтверждения). */
  initial: Program[]
  programs: Program[]
  sectionOf: (p: Program) => string
  onNext: (to: Program[]) => void
  onClose: () => void
}) {
  const [chosen, setChosen] = useState<Program[]>(initial)
  const [picking, setPicking] = useState(false)
  const chosenIds = new Set(chosen.map((p) => p.id))
  const title = 'Разложить на программы'
  return (
    <>
      {/* Поиск программы открывается поверх выбора; закрылся — фокус возвращается в выбор. */}
      <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
        <DialogContent title={title} className="max-w-2xl">
          <Card header={title}>
            <p className="mb-3 text-14 text-txt-secondary">
              Прежняя: «{from.name}». Выберите программы каталога, из которых она состояла, — не меньше двух.
            </p>
            {chosen.length > 0 ? (
              <ul className="mb-2 divide-y divide-line rounded-8 border border-line" aria-label="Программы состава">
                {chosen.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 px-3 py-2">
                    <span className="min-w-0 flex-1 break-words text-14 text-txt">
                      <span className="mr-2 text-txt-secondary">{p.code}</span>
                      {programLine(p)}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      aria-label={`Убрать «${programLine(p)}»`}
                      title="Убрать"
                      onClick={() => setChosen((prev) => prev.filter((x) => x.id !== p.id))}
                      leftIcon={<X className="size-4 stroke-current" aria-hidden />}
                    />
                  </li>
                ))}
              </ul>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setPicking(true)}
              leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
            >
              Добавить программу
            </Button>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>
                Отмена
              </Button>
              <Button disabled={chosen.length < 2} onClick={() => onNext(chosen)}>
                Далее
              </Button>
            </div>
          </Card>
        </DialogContent>
      </Dialog>
      {picking ? (
        <ProgramPickerDialog
          title="Программа состава"
          subtitle={`Прежняя: «${from.name}»`}
          programs={programs.filter((p) => !chosenIds.has(p.id))}
          sectionOf={sectionOf}
          onClose={() => setPicking(false)}
          onPick={(p) => {
            setChosen((prev) => [...prev, p])
            setPicking(false)
          }}
        />
      ) : null}
    </>
  )
}
